import { useEffect, useRef } from 'react';

const COOKIE_VARIANT = 'bkt_variant';
const COOKIE_USER_ID = 'bkt_user_id';
const COOKIE_MAX_AGE = 2592000; // 30 days

/**
 * After authentication, binds the real user_id to the existing A/B variant cookie.
 *
 * The variant assigned by the Cloudflare Worker (Phase 1) is always preserved.
 * This hook only updates the bkt_user_id cookie so the Worker recognizes the
 * user on subsequent visits. It never re-evaluates Bucketeer or switches variants,
 * preventing jarring UI changes between sessions.
 *
 * @param {string|undefined} userId - The authenticated user's ID, or undefined if not logged in.
 */
export function useABVariantSync(userId) {
  const hasReconciled = useRef(false);

  useEffect(() => {
    if (!userId || hasReconciled.current) return;

    hasReconciled.current = true;

    try {
      const currentVariant = getCookie(COOKIE_VARIANT);
      const currentBktUserId = getCookie(COOKIE_USER_ID);

      // Already bound - nothing to do
      if (currentBktUserId === userId) return;

      // Bind the real user_id to the current variant without re-evaluating.
      // The user keeps whichever variant the Worker assigned.
      console.log(
        `[AB Test] Binding user ${userId} to current variant "${currentVariant}"`
      );
      setCookie(COOKIE_USER_ID, userId, COOKIE_MAX_AGE);
    } catch (err) {
      // Never break the app over A/B testing
      console.warn('[AB Test] User ID binding failed:', err);
    }
  }, [userId]);
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name, value, maxAge) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; secure; samesite=lax`;
}
